import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { Dumbbell, CheckCircle, Camera, User, Phone, Mail, MapPin, Heart, Target, PenTool, RotateCcw, Globe, Info, Building2, Unlink2, Scale, Ruler, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const goals = ["Muscle Building", "Weight Loss", "Weight Gain", "General Fitness", "Other"];
const relations = ["Father", "Mother", "Spouse", "Brother", "Sister", "Friend", "Other"];

const GOAL_TYPES = [
  { value: "Weight Loss", label: "Weight Loss" },
  { value: "Weight Gain", label: "Weight Gain" },
  { value: "Muscle Building", label: "Muscle Building" },
  { value: "General Fitness", label: "General Fitness" },
  { value: "Custom", label: "Custom" },
];

const MEASUREMENTS = [
  { id: "weight", label: "Weight", unit: "kg", icon: Scale },
  { id: "chest", label: "Chest", unit: "cm", icon: Ruler },
  { id: "waist", label: "Waist", unit: "cm", icon: Ruler },
  { id: "arm", label: "Arm", unit: "cm", icon: Ruler },
  { id: "bmi", label: "BMI", unit: "", icon: Scale },
];

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidUUID = (val) => UUID_REGEX.test(val);

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const daysInMonth = (month, year) => {
  if (!month || !year) return 31;
  return new Date(year, month, 0).getDate();
};

const getErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Registration failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => {
      const field = Array.isArray(d.loc) ? d.loc.filter((l) => l !== "body").join(" \u2192 ") : "";
      return field ? `${field}: ${d.msg}` : d.msg;
    }).join("; ");
  }
  return "Registration failed";
};

function DobPicker({ value, onChange }) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const parts = value ? value.split("-") : [];
  const [day, setDay] = useState(parts[2] ? String(parseInt(parts[2])) : "");
  const [month, setMonth] = useState(parts[1] ? String(parseInt(parts[1])) : "");
  const [year, setYear] = useState(parts[0] || "");

  const emit = (d, m, y) => {
    if (d && m && y) onChange(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    else onChange("");
  };

  const handleYear = (y) => {
    setYear(y);
    const maxD = daysInMonth(parseInt(month), parseInt(y));
    const cd = day && parseInt(day) > maxD ? "" : day;
    setDay(cd); emit(cd, month, y);
  };
  const handleMonth = (m) => {
    setMonth(m);
    const maxD = daysInMonth(parseInt(m), parseInt(year));
    const cd = day && parseInt(day) > maxD ? "" : day;
    setDay(cd); emit(cd, m, year);
  };
  const handleDay = (d) => { setDay(d); emit(d, month, year); };

  const years = [];
  for (let y = currentYear; y >= 1920; y--) years.push(y);
  const maxMonth = year && parseInt(year) === currentYear ? today.getMonth() + 1 : 12;
  const maxDayCount = daysInMonth(parseInt(month), parseInt(year));
  const maxDay = year && month && parseInt(year) === currentYear && parseInt(month) === today.getMonth() + 1
    ? today.getDate() : maxDayCount;

  return (
    <div className="grid grid-cols-3 gap-2">
      <Select value={day} onValueChange={handleDay}>
        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Day" /></SelectTrigger>
        <SelectContent>
          {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
            <SelectItem key={d} value={String(d)}>{d}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={month} onValueChange={handleMonth}>
        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Month" /></SelectTrigger>
        <SelectContent>
          {MONTHS.slice(0, maxMonth).map((m, i) => (
            <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={year} onValueChange={handleYear}>
        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Year" /></SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const getSignatureDataUrl = (sigRef) => {
  if (!sigRef.current || sigRef.current.isEmpty()) return null;
  try {
    return sigRef.current.getCanvas().toDataURL("image/png");
  } catch {
    return null;
  }
};

// ── Social icon SVGs (inline, no extra dep) ──────────────────────────────────
function FacebookIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
    </svg>
  );
}
function InstagramIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  );
}
function XIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// Shown when orgId or branchId are not valid UUIDs
function InvalidLinkPage() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="bg-card border-border rounded-sm max-w-md w-full text-center">
        <CardContent className="p-8">
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
            <Unlink2 className="h-10 w-10 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold font-heading">Invalid Registration Link</h2>
          <p className="text-muted-foreground mt-3 text-sm">
            This signup link appears to be broken or incomplete. Please scan the QR code again or ask your gym for the correct link.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PublicSignup() {
  const { orgId, branchId } = useParams();
  if (!isValidUUID(orgId) || !isValidUUID(branchId)) {
    return <InvalidLinkPage />;
  }
  return <SignupForm orgId={orgId} branchId={branchId} />;
}

function SignupForm({ orgId, branchId }) {
  const [orgInfo, setOrgInfo] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const signatureRef = useRef(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", dob: "", gender: "Male",
    address: "", state: "", city: "", pincode: "",
    emergencyContactName: "", emergencyContactRelation: "", emergencyContactPhone: "",
    medicalIssue: "", goal: "General Fitness", height: "", weight: "",
    joiningDate: new Date().toISOString().slice(0, 10), photo: "", signature: "",
  });

  const [createGoalEnabled, setCreateGoalEnabled] = useState(true);
  const [goalForm, setGoalForm] = useState({
    name: "",
    description: "",
    goalType: "Weight Loss",
    reminderDays: 7,
  });
  const [selectedMeasurements, setSelectedMeasurements] = useState([]);

  const handleGoalToggle = (checked) => {
    setCreateGoalEnabled(checked);
    if (checked) {
      const initialWeight = parseFloat(form.weight);
      const initialHeight = parseFloat(form.height);
      setSelectedMeasurements(prev => {
        let list = [...prev];
        if (initialWeight && !list.some(m => m.metric === "weight")) {
          list.push({ metric: "weight", currentValue: initialWeight, targetValue: 0 });
        }
        if (initialHeight && initialWeight) {
          const bmiVal = parseFloat((initialWeight / ((initialHeight / 100) * (initialHeight / 100))).toFixed(1));
          if (bmiVal && !list.some(m => m.metric === "bmi")) {
            list.push({ metric: "bmi", currentValue: bmiVal, targetValue: 0 });
          }
        }
        return list;
      });
    }
  };

  useEffect(() => {
    axios.get(`${API}/branches/public/org/${orgId}`)
      .then((r) => setOrgInfo(r.data.organization))
      .catch(console.error);
    axios.get(`${API}/branches/public/${orgId}/${branchId}`)
      .then((r) => setBranchInfo(r.data.branch))
      .catch(console.error);
  }, [orgId, branchId]);

  const update = (key, val) => {
    setForm((p) => {
      const updated = { ...p, [key]: val };
      
      const w = key === "weight" ? parseFloat(val) || 0 : parseFloat(p.weight) || 0;
      const h = key === "height" ? parseFloat(val) || 0 : parseFloat(p.height) || 0;
      let calculatedBmi = 0;
      if (w > 0 && h > 0) {
        calculatedBmi = parseFloat((w / ((h / 100) * (h / 100))).toFixed(1));
      }

      if (key === "weight" || key === "height") {
        setSelectedMeasurements(prev => {
          let updatedMeasurements = [...prev];
          
          if (key === "weight" && w > 0) {
            const hasWeight = updatedMeasurements.some(m => m.metric === "weight");
            if (hasWeight) {
              updatedMeasurements = updatedMeasurements.map(m => m.metric === "weight" ? { ...m, currentValue: w } : m);
            } else if (createGoalEnabled) {
              updatedMeasurements.push({ metric: "weight", currentValue: w, targetValue: 0 });
            }
          }
          
          if (calculatedBmi > 0) {
            const hasBmi = updatedMeasurements.some(m => m.metric === "bmi");
            if (hasBmi) {
              updatedMeasurements = updatedMeasurements.map(m => m.metric === "bmi" ? { ...m, currentValue: calculatedBmi } : m);
            }
          }
          
          return updatedMeasurements;
        });
      }
      return updated;
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be less than 5MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result);
    reader.readAsDataURL(file);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload`, formData);
      update("photo", res.data.url);
      toast.success("Photo uploaded");
    } catch { toast.error("Failed to upload photo"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) { toast.error("Please agree to terms"); return; }
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    if (!form.dob) { toast.error("Date of birth is required"); return; }
    if (!form.address.trim()) { toast.error("Address is required"); return; }
    if (!form.emergencyContactName.trim() || !form.emergencyContactPhone.trim()) {
      toast.error("Emergency contact name and phone are required"); return;
    }
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error("Please provide your signature"); return;
    }
    const signatureData = getSignatureDataUrl(signatureRef);
    if (!signatureData) { toast.error("Signature could not be captured, please try again"); return; }

    if (createGoalEnabled) {
      if (!goalForm.name.trim()) {
        toast.error("Goal name is required");
        return;
      }
      if (selectedMeasurements.length === 0) {
        toast.error("Select at least one measurement to track for the goal");
        return;
      }
    }

    const payload = {
      ...form,
      signature: signatureData,
      height: form.height !== "" ? parseFloat(form.height) : null,
      weight: form.weight !== "" ? parseFloat(form.weight) : null,
      createGoalEnabled,
      goalName: goalForm.name.trim(),
      goalDescription: goalForm.description,
      goalType: goalForm.goalType,
      goalReminderDays: goalForm.reminderDays,
      goalMeasurements: selectedMeasurements,
    };

    setLoading(true);
    try {
      await axios.post(`${API}/members/public-signup?orgId=${orgId}&branchId=${branchId}`, payload);
      setSuccess(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) signatureRef.current.clear();
    update("signature", "");
  };

  if (success) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="bg-card border-border rounded-sm max-w-md w-full text-center">
        <CardContent className="p-8">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold font-heading">Registration Successful!</h2>
          <p className="text-muted-foreground mt-3">
            Your registration has been submitted. Gym staff will review and activate your membership.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            You will receive a confirmation once your membership is active.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  const logoSrc = orgInfo?.logo ? resolveUrl(orgInfo.logo) : null;
  const whatsapp = orgInfo?.whatsapp;
  const facebook = orgInfo?.facebook;
  const instagram = orgInfo?.instagram;
  const twitter = orgInfo?.twitter;
  const hasSocial = facebook || instagram || twitter;

  // Branch details (prefer branch-level, fall back to org-level)
  const branchName = branchInfo?.name;
  const branchPhone = branchInfo?.phone;
  const branchAddress = branchInfo?.address;

  // Org-level contact (shown separately from branch)
  const orgPhone = orgInfo?.phone;
  const orgLocation = orgInfo?.location;

  const heightVal = parseFloat(form.height);
  const weightVal = parseFloat(form.weight);
  let bmi = null;
  let bmiCategory = "";
  let bmiColorClass = "";

  if (heightVal > 0 && weightVal > 0) {
    const heightInMeters = heightVal / 100;
    bmi = (weightVal / (heightInMeters * heightInMeters)).toFixed(1);
    const bmiNum = parseFloat(bmi);
    if (bmiNum < 18.5) {
      bmiCategory = "Underweight";
      bmiColorClass = "text-yellow-500 bg-yellow-500/10 border-yellow-500/20";
    } else if (bmiNum < 25) {
      bmiCategory = "Normal Weight";
      bmiColorClass = "text-green-500 bg-green-500/10 border-green-500/20";
    } else if (bmiNum < 30) {
      bmiCategory = "Overweight";
      bmiColorClass = "text-orange-500 bg-orange-500/10 border-orange-500/20";
    } else {
      bmiCategory = "Obese";
      bmiColorClass = "text-red-500 bg-red-500/10 border-red-500/20";
    }
  }

  const socialHref = (handle, base) => {
    if (!handle) return "#";
    if (handle.startsWith("http")) return handle;
    return `${base}${handle}`;
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-2xl mx-auto">

        {/* ── Header Card ── */}
        <div className="bg-card border border-border rounded-sm mb-6 overflow-hidden">

          {/* ── TOP: Org branding row ── */}
          <div className="p-5">
            <div className="flex items-start gap-4">
              {/* Logo */}
              <div className="shrink-0">
                {logoSrc ? (
                  <img src={logoSrc} alt={orgInfo.name} className="h-20 w-20 rounded-sm object-cover border border-border" />
                ) : (
                  <div className="h-20 w-20 rounded-sm bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <Dumbbell className="h-9 w-9 text-primary" />
                  </div>
                )}
              </div>

              {/* Org info */}
              <div className="flex-1 min-w-0 space-y-1.5">
                {/* Gym name */}
                <h1 className="text-xl md:text-2xl font-bold font-heading text-primary leading-tight">
                  {orgInfo?.name || "Gym"}
                </h1>

                {/* Description */}
                {orgInfo?.description && (
                  <p className="text-xs text-muted-foreground flex items-start gap-1">
                    <Info className="h-3 w-3 mt-0.5 shrink-0" />
                    {orgInfo.description}
                  </p>
                )}

                {/* Org contact row */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-0.5">
                  {orgPhone && (
                    <a href={`tel:${orgPhone}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                      <Phone className="h-3 w-3" /> {orgPhone}
                    </a>
                  )}
                  {whatsapp && (
                    <a
                      href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-green-500 hover:text-green-400 transition-colors"
                    >
                      <WhatsAppIcon className="h-3 w-3" /> {whatsapp}
                    </a>
                  )}
                  {orgInfo?.email && (
                    <a href={`mailto:${orgInfo.email}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                      <Mail className="h-3 w-3" /> {orgInfo.email}
                    </a>
                  )}
                  {orgInfo?.website && (
                    <a
                      href={orgInfo.website.startsWith("http") ? orgInfo.website : `https://${orgInfo.website}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-primary hover:underline transition-colors"
                    >
                      <Globe className="h-3 w-3" /> {orgInfo.website}
                    </a>
                  )}
                  {orgLocation && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" /> {orgLocation}
                    </span>
                  )}
                </div>

                {/* Social icons */}
                {hasSocial && (
                  <div className="flex items-center gap-2 pt-1">
                    {facebook && (
                      <a href={socialHref(facebook, "https://facebook.com/")} target="_blank" rel="noopener noreferrer"
                        className="h-7 w-7 rounded-sm bg-blue-600/10 hover:bg-blue-600/20 flex items-center justify-center text-blue-500 transition-colors" title="Facebook">
                        <FacebookIcon className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {instagram && (
                      <a href={socialHref(instagram, "https://instagram.com/")} target="_blank" rel="noopener noreferrer"
                        className="h-7 w-7 rounded-sm bg-pink-500/10 hover:bg-pink-500/20 flex items-center justify-center text-pink-500 transition-colors" title="Instagram">
                        <InstagramIcon className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {twitter && (
                      <a href={socialHref(twitter, "https://x.com/")} target="_blank" rel="noopener noreferrer"
                        className="h-7 w-7 rounded-sm bg-foreground/10 hover:bg-foreground/20 flex items-center justify-center text-foreground transition-colors" title="X (Twitter)">
                        <XIcon className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── BOTTOM: Branch details strip ── */}
          {branchInfo && (
            <div className="border-t border-border bg-muted/40 px-5 py-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Building2 className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold text-primary uppercase tracking-wide">Branch</span>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                {/* Branch name */}
                {branchName && (
                  <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    {branchName}
                  </span>
                )}
                {/* Branch phone — clickable */}
                {branchPhone && (
                  <a
                    href={`tel:${branchPhone}`}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    {branchPhone}
                  </a>
                )}
                {/* Branch address */}
                {branchAddress && (
                  <span className="flex items-start gap-1.5 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    {branchAddress}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <Card className="bg-card border-border rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Member Registration</CardTitle>
            <CardDescription>Fill in your details to join our gym</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Photo Upload */}
              <div className="flex flex-col items-center">
                <div className="relative cursor-pointer group" onClick={() => fileInputRef.current?.click()}>
                  <Avatar className="h-24 w-24 border-2 border-dashed border-border group-hover:border-primary transition-colors">
                    {photoPreview ? <AvatarImage src={photoPreview} /> : null}
                    <AvatarFallback className="bg-secondary">
                      <Camera className="h-8 w-8 text-muted-foreground group-hover:text-primary transition-colors" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <Camera className="h-4 w-4 text-primary-foreground" />
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                <p className="text-xs text-muted-foreground mt-2">Click to upload photo</p>
              </div>

              <Separator />

              {/* Basic Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <User className="h-4 w-4" /> Basic Information
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input value={form.name} onChange={(e) => update("name", e.target.value)} required className="rounded-sm mt-1" placeholder="John Doe" />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <PhoneInput
                      defaultCountry="in"
                      value={form.phone}
                      onChange={(v) => update("phone", v || "")}
                      inputClassName="flex h-10 w-full rounded-sm border border-input bg-secondary px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-sm mt-1" placeholder="john@email.com" />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select value={form.gender} onValueChange={(v) => update("gender", v)}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Date of Birth *</Label>
                    <div className="mt-1">
                      <DobPicker value={form.dob} onChange={(v) => update("dob", v)} />
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Address */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <MapPin className="h-4 w-4" /> Address
                </div>
                <div>
                  <Label>Street Address *</Label>
                  <Textarea value={form.address} onChange={(e) => update("address", e.target.value)} className="rounded-sm mt-1" placeholder="123, Main Street" rows={2} required />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><Label>City</Label><Input value={form.city} onChange={(e) => update("city", e.target.value)} className="rounded-sm mt-1" /></div>
                  <div><Label>State</Label><Input value={form.state} onChange={(e) => update("state", e.target.value)} className="rounded-sm mt-1" /></div>
                  <div><Label>Pincode</Label><Input value={form.pincode} onChange={(e) => update("pincode", e.target.value)} className="rounded-sm mt-1" /></div>
                </div>
              </div>

              <Separator />

              {/* Emergency Contact */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Phone className="h-4 w-4" /> Emergency Contact *
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input placeholder="Contact Name" value={form.emergencyContactName} onChange={(e) => update("emergencyContactName", e.target.value)} className="rounded-sm mt-1" required />
                  </div>
                  <div>
                    <Label>Relation</Label>
                    <Select value={form.emergencyContactRelation} onValueChange={(v) => update("emergencyContactRelation", v)}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{relations.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <PhoneInput
                      defaultCountry="in"
                      value={form.emergencyContactPhone}
                      onChange={(v) => update("emergencyContactPhone", v || "")}
                      inputClassName="flex h-10 w-full rounded-sm border border-input bg-secondary px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Fitness Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Target className="h-4 w-4" /> Fitness Information
                </div>

                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border mt-4">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <Target className="h-4 w-4 text-primary" />
                      Set Detailed Fitness Goal
                    </Label>
                    <p className="text-xs text-muted-foreground">Track detailed targets (chest, waist, arm, etc.) with regular check-in reminders</p>
                  </div>
                  <Switch
                    checked={createGoalEnabled}
                    onCheckedChange={handleGoalToggle}
                    type="button"
                    data-testid="create-goal-toggle"
                  />
                </div>

                {createGoalEnabled && (
                  <div className="space-y-4 pt-2 border-t border-border/60 animate-in fade-in duration-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Goal Name *</Label>
                        <Input
                          placeholder="e.g. Lose 10kg"
                          value={goalForm.name}
                          onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                          className="rounded-sm mt-1"
                          data-testid="goal-name-input"
                        />
                      </div>
                      <div>
                        <Label>Goal Type</Label>
                        <Select value={goalForm.goalType} onValueChange={(v) => setGoalForm({ ...goalForm, goalType: v })}>
                          <SelectTrigger className="rounded-sm mt-1" data-testid="goal-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {GOAL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Textarea
                        placeholder="Add details about this goal..."
                        value={goalForm.description}
                        onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                        className="rounded-sm mt-1 min-h-[60px]"
                      />
                    </div>

                    {/* Measurement selections */}
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                        Select Measurements to Track
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {MEASUREMENTS.map(m => {
                          const isSelected = selectedMeasurements.some(sm => sm.metric === m.id);
                          return (
                            <Badge
                              key={m.id}
                              variant={isSelected ? "default" : "outline"}
                              className={`cursor-pointer px-3 py-1.5 rounded-sm ${isSelected ? 'bg-primary text-primary-foreground' : ''}`}
                              onClick={() => {
                                const exists = selectedMeasurements.find(sm => sm.metric === m.id);
                                if (exists) {
                                  setSelectedMeasurements(selectedMeasurements.filter(sm => sm.metric !== m.id));
                                } else {
                                  setSelectedMeasurements([...selectedMeasurements, {
                                    metric: m.id,
                                    currentValue: m.id === "weight" ? (parseFloat(form.weight) || 70) : 0,
                                    targetValue: 0,
                                  }]);
                                }
                              }}
                            >
                              <m.icon className="h-3 w-3 mr-1" />
                              {m.label}
                              {isSelected && <X className="h-3 w-3 ml-1" />}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>

                    {/* Measurement Values input */}
                    {selectedMeasurements.length > 0 && (
                      <div className="space-y-3">
                        <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                          Set Target Values
                        </Label>
                        <div className="space-y-2">
                          {selectedMeasurements.map(sm => {
                            const mInfo = MEASUREMENTS.find(m => m.id === sm.metric);
                            return (
                              <div key={sm.metric} className="grid grid-cols-3 gap-2 p-2 bg-secondary/20 border border-border/40 rounded-sm items-center">
                                <div>
                                  <p className="text-xs font-medium flex items-center gap-1.5">
                                    <mInfo.icon className="h-3.5 w-3.5 text-primary" />
                                    {mInfo.label} {mInfo.unit ? `(${mInfo.unit})` : ''}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-[10px] text-muted-foreground font-mono uppercase shrink-0">Cur</Label>
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={sm.currentValue}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value) || 0;
                                      setSelectedMeasurements(selectedMeasurements.map(item =>
                                        item.metric === sm.metric ? { ...item, currentValue: val } : item
                                      ));
                                    }}
                                    className="h-8 rounded-sm text-xs"
                                  />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-[10px] text-muted-foreground font-mono uppercase shrink-0">Tgt</Label>
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={sm.targetValue}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value) || 0;
                                      setSelectedMeasurements(selectedMeasurements.map(item =>
                                        item.metric === sm.metric ? { ...item, targetValue: val } : item
                                      ));
                                    }}
                                    className="h-8 rounded-sm text-xs font-semibold border-primary/40 focus:border-primary"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Reminder Days */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                          Progress Reminder
                        </Label>
                        <Badge variant="outline" className="font-mono text-[10px] rounded-sm py-0">
                          Every {goalForm.reminderDays} days
                        </Badge>
                      </div>
                      <Slider
                        value={[goalForm.reminderDays]}
                        onValueChange={(v) => setGoalForm({ ...goalForm, reminderDays: v[0] })}
                        min={1}
                        max={31}
                        step={1}
                        className="w-full py-2"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Height (cm)</Label><Input type="number" value={form.height} onChange={(e) => update("height", e.target.value)} className="rounded-sm mt-1" placeholder="175" /></div>
                <div><Label>Weight (kg)</Label><Input type="number" value={form.weight} onChange={(e) => update("weight", e.target.value)} className="rounded-sm mt-1" placeholder="70" /></div>
              </div>

              {bmi && (
                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border mt-2 animate-in fade-in duration-200">
                  <div className="flex items-center gap-2">
                    <Scale className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Calculated BMI</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-bold">{bmi}</span>
                    <Badge variant="outline" className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${bmiColorClass}`}>
                      {bmiCategory}
                    </Badge>
                  </div>
                </div>
              )}

              <Separator />

              {/* Medical */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Heart className="h-4 w-4" /> Medical Information
                </div>
                <div>
                  <Label>Any Medical Conditions / Allergies</Label>
                  <Textarea value={form.medicalIssue} onChange={(e) => update("medicalIssue", e.target.value)} className="rounded-sm mt-1" placeholder="Please mention any medical conditions, injuries, or allergies..." rows={2} />
                </div>
              </div>

              <Separator />

              {/* Signature */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <PenTool className="h-4 w-4" /> Signature *
                </div>
                <div className="border border-border rounded-lg bg-white p-2">
                  <SignatureCanvas
                    ref={signatureRef}
                    penColor="black"
                    canvasProps={{ className: "w-full h-32 rounded", style: { touchAction: "none" } }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Draw your signature above</p>
                  <Button type="button" variant="ghost" size="sm" onClick={clearSignature} className="text-muted-foreground text-xs">
                    <RotateCcw className="h-3 w-3 mr-1" /> Clear
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Terms */}
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
                <span className="text-sm text-muted-foreground">
                  I agree to the Terms &amp; Conditions and confirm that all the information provided is accurate. I understand that my membership is subject to gym rules and regulations.
                </span>
              </label>

              <Button type="submit" className="w-full rounded-sm h-12 text-base" disabled={loading || !agreed}>
                {loading ? "Submitting..." : "Complete Registration"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Powered by Optimus Gym CRM
        </p>
      </div>
    </div>
  );
}
